import type { NtSelection, NtStudent } from '../../core/types'
import { classYearFromEmail, gradeFor } from '../../core/students'

/**
 * Course selections, as placement reads them.
 *
 * Students make their selections in the app (ntSelections). The CSV import
 * (ntStudents) is the fallback, for a term where some students used the
 * Google Form. Placement wants one list, so this merges them: a student who
 * selected in the app is taken from there, timestamp and all; anyone else
 * comes from the import. Pure, so it is tested (tests/selections.test.ts).
 */

const toIso = (v: unknown): string | undefined => {
  const d = v && typeof (v as { toDate?: () => Date }).toDate === 'function'
    ? (v as { toDate: () => Date }).toDate()
    : v instanceof Date ? v : null
  return d && !isNaN(d.getTime()) ? d.toISOString() : undefined
}

export function mergeSelections(imported: NtStudent[], selections: NtSelection[], now: Date = new Date()): NtStudent[] {
  const fromApp = selections
    .filter((s) => s.ranks.length > 0)
    .map((s): NtStudent => {
      const year = classYearFromEmail(s.studentEmail)
      const submittedAt = toIso(s.submittedAt)
      return {
        id: s.id, termId: s.termId, studentId: s.studentId, name: s.studentName,
        grade: year ? gradeFor(year, now) : 9, ranks: s.ranks,
        ...(submittedAt ? { submittedAt } : {}),
      }
    })
  const inApp = new Set(fromApp.map((s) => s.studentId))
  return [...fromApp, ...imported.filter((s) => !inApp.has(s.studentId))]
}

/** Whether a student's selections count as submitted: complete, and stamped. */
export const isSubmitted = (s: NtSelection | null | undefined, required: number) =>
  !!s && !!s.submittedAt && s.ranks.length >= required
